/**
 * Minimal allowlist HTML sanitizer for the mobile feedback view.
 *
 * Scope: the safe subset of HTML that JeddAI responses / summaries and the
 * Quill editor emit (p, lists, basic inline formatting, links). It is NOT a
 * general-purpose sanitizer — it exists so the mobile view can render a
 * student's own response HTML without a `dangerouslySetInnerHTML` XSS vector
 * (the desktop avoids this by rendering through a read-only Quill instance;
 * on mobile we inject footnote anchors, so we sanitize instead).
 *
 * Anything outside the allowlist is unwrapped to its text content; all
 * event-handler attributes and javascript:/data: URLs are dropped.
 */

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'SPAN',
  'A',
  'SUP',
  'SUB',
  'DIV',
  'MARK',
]);

// `class` is deliberately NOT allowed wholesale: arbitrary classes on
// span/div/mark would let injected content borrow privileged .jdm classes
// (jdm-sheet, jdm-scrim, jdm-toast…) and overlay or spoof the mobile UI.
//
// But Quill encodes real SEMANTICS in a small set of its own classes and data
// attributes — alignment and list type. Dropping them means opening a
// desktop-authored answer on mobile and making any edit silently converts a
// numbered list to bullets, or loses centring, on save. So those specific
// values are allowed through, by exact match only: a class is kept only if it
// is one of Quill's, never merely because it starts with "ql-".
const ALLOWED_ATTRS = {
  A: new Set(['href']),
};

// Immutable historical records also retain tables, code and stored images.
// Opt-in keeps the existing mobile editor's serialization contract unchanged.
const RICH_TAGS = new Set(['TABLE', 'CAPTION', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'PRE', 'CODE', 'IMG']);
const RICH_ATTRS = {
  IMG: new Set(['src', 'alt']),
  TH: new Set(['colspan', 'rowspan']),
  TD: new Set(['colspan', 'rowspan']),
};
// Tables alone, without the images RICH_TAGS also admits: a remote image is a
// tracking pixel when the author is not the viewer.
const TABLE_TAGS = new Set(['TABLE', 'CAPTION', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD']);

const QUILL_CLASSES = new Set([
  'ql-align-center',
  'ql-align-right',
  'ql-align-justify',
  'ql-indent-1',
  'ql-indent-2',
  'ql-indent-3',
  'ql-indent-4',
  'ql-indent-5',
  'ql-indent-6',
  'ql-indent-7',
  'ql-indent-8',
]);
// Quill's size picker, which the desktop stimulus views style. Opt-in so the
// mobile editor's serialization contract stays unchanged.
const QUILL_SIZE_CLASSES = new Set([
  'ql-size-small',
  'ql-size-large',
  'ql-size-huge',
]);
// Quill 2 marks list type with data-list="bullet|ordered|unchecked|checked".
const QUILL_DATA_LIST = new Set(['bullet', 'ordered', 'unchecked', 'checked']);

// Opt-in inline colours (text colour and highlight). Only these two
// declarations survive, and only with a numeric colour function, hex or keyword
// value, so a style can neither load anything (no url()) nor reposition content
// over the app's own UI.
const COLOR_PROPS = new Set(['color', 'background-color']);
const SAFE_COLOR_VALUE =
  /^((rgba?|hsla?|hwb|lab|lch|oklab|oklch)\([\d.,%\s/+-]+\)|#[0-9a-f]{3,8}|[a-z]+)$/;

/** Keep only colour declarations with a plain colour value; drop the rest. */
function filterColorStyle(value) {
  return String(value || '')
    .split(';')
    .map((declaration) => {
      const colon = declaration.indexOf(':');
      if (colon < 0) return null;
      const prop = declaration.slice(0, colon).trim().toLowerCase();
      const val = declaration.slice(colon + 1).trim().toLowerCase();
      return COLOR_PROPS.has(prop) && SAFE_COLOR_VALUE.test(val)
        ? `${prop}: ${val}`
        : null;
    })
    .filter(Boolean)
    .join('; ');
}

/** Keep only Quill's own semantic classes; drop everything else. */
function filterClasses(value, preserveQuillSizes) {
  return String(value || '')
    .split(/\s+/)
    .filter(
      (c) =>
        QUILL_CLASSES.has(c) ||
        (preserveQuillSizes && QUILL_SIZE_CLASSES.has(c))
    )
    .join(' ');
}

export function sanitizeHtml(
  html,
  {
    preserveRichContent = false,
    preserveQuillSizes = false,
    preserveColors = false,
    preserveTables = false,
    // Drops <img> even under preserveRichContent. For content another user
    // wrote that the viewer has not accepted (a share preview), where a remote
    // image is a tracking pixel.
    dropImages = false,
  } = {}
) {
  if (!html) return '';
  // No-DOM fallback (SSR / non-browser test env): strip all tags.
  if (typeof document === 'undefined' || !document.createElement) {
    return String(html).replace(/<[^>]*>/g, '');
  }
  const tpl = document.createElement('template');
  tpl.innerHTML = String(html);

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 1) {
        const tag = child.tagName;
        if (
          (dropImages && tag === 'IMG') ||
          (!ALLOWED_TAGS.has(tag) &&
            !(preserveRichContent && RICH_TAGS.has(tag)) &&
            !(preserveTables && TABLE_TAGS.has(tag)))
        ) {
          // Unwrap disallowed element to plain text (drops script/style/iframe…).
          child.replaceWith(document.createTextNode(child.textContent || ''));
          return;
        }
        const allowed =
          ALLOWED_ATTRS[tag] ||
          ((preserveRichContent || preserveTables) && RICH_ATTRS[tag]) ||
          new Set();
        Array.from(child.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (name === 'class') {
            // Exact-match allowlist — never a prefix test.
            const kept = filterClasses(attr.value, preserveQuillSizes);
            if (kept) child.setAttribute('class', kept);
            else child.removeAttribute(attr.name);
            return;
          }
          if (name === 'style' && preserveColors) {
            const kept = filterColorStyle(attr.value);
            if (kept) child.setAttribute('style', kept);
            else child.removeAttribute(attr.name);
            return;
          }
          if (name === 'data-list' && tag === 'LI') {
            if (!QUILL_DATA_LIST.has(String(attr.value)))
              child.removeAttribute(attr.name);
            return;
          }
          if (!allowed.has(name)) {
            child.removeAttribute(attr.name);
            return;
          }
          if (name === 'src') {
            try {
              const url = new URL(attr.value, window.location.href);
              if (url.protocol !== 'https:' || url.username || url.password) child.removeAttribute(attr.name);
              else child.setAttribute('src', url.href);
            } catch (_) { child.removeAttribute(attr.name); }
          }
          if ((name === 'colspan' || name === 'rowspan') && !/^[1-9][0-9]?$/.test(attr.value)) child.removeAttribute(attr.name);
          if (name === 'href') {
            // Strip ASCII control + whitespace FIRST so tab/newline-obfuscated
            // schemes (e.g. "java&#x09;script:…", which the DOM decodes to a
            // real tab) can't slip past, then allow only a positive protocol
            // allowlist or a relative/anchor URL.
            const norm = (attr.value || '')
              .replace(/[\u0000-\u0020\u007f]+/g, '')
              .toLowerCase();
            const hasScheme = /^[a-z][a-z0-9+.-]*:/.test(norm);
            const safe =
              /^(https?:|mailto:|tel:)/.test(norm) ||
              !hasScheme ||
              /^[/#.]/.test(norm);
            if (!safe) child.removeAttribute(attr.name);
          }
        });
        if (tag === 'A' && child.getAttribute('href')) {
          child.setAttribute('rel', 'noopener noreferrer');
          child.setAttribute('target', '_blank');
        }
        walk(child);
      } else if (child.nodeType === 8) {
        child.remove(); // strip comments
      }
    });
  };

  walk(tpl.content);
  return tpl.innerHTML;
}
